import {
  LayoutDashboard, Building2, Users, FileText, Palette, Briefcase,
  Search, Settings, Image as ImageIcon, BarChart3, MapPin, Layers,
  Wallet, UserCheck, CalendarCheck,
} from "lucide-react";

/* RBAC role registry — mirrors the AbhiGhar admin permission matrix.
   super_admin owns everything; admin handles operational management;
   employee (previously "sales") sees only their own assigned leads. */
export const ROLES = {
  super_admin: { label: "Super Admin", desc: "Platform owner · full access" },
  admin: { label: "Admin", desc: "Operational management" },
  employee: { label: "Employee", desc: "Assigned leads only" },
};

/* Complete navigation tree. Each top-level item declares which roles can
   see it; children inherit the role gating. Identical structure to the old
   admin so muscle-memory carries over.

   Note on ids: route ids like `sales_team`, `sales_team.all` are KEPT
   even though the role was renamed to `employee`. They're internal
   keys that AdminShell uses to pick which screen to render; renaming
   them would require changes across every page file. Label-only
   change is enough for the rename to be visible to users. */
export const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin", "employee"] },
  {
    id: "properties", label: "Properties", icon: Building2, roles: ["super_admin", "admin"],
    children: [
      { id: "properties.all", label: "All Projects", roles: ["super_admin", "admin"] },
      { id: "properties.new", label: "Add Project", roles: ["super_admin", "admin"] },
      { id: "properties.drafts", label: "Drafts", roles: ["super_admin", "admin"] },
    ],
  },
  { id: "builders", label: "Builders", icon: Layers, roles: ["super_admin", "admin"] },
  {
    id: "locations", label: "Locations", icon: MapPin, roles: ["super_admin", "admin"],
    children: [
      { id: "locations.cities", label: "Cities", roles: ["super_admin", "admin"] },
      { id: "locations.localities", label: "Localities", roles: ["super_admin", "admin"] },
    ],
  },
  {
    id: "leads", label: "Real Estate Leads", icon: Users, roles: ["super_admin", "admin", "employee"],
    children: [
      { id: "leads.all", label: "All Leads", roles: ["super_admin", "admin", "employee"] },
      { id: "leads.settings", label: "Lead Settings", roles: ["super_admin", "admin"] },
    ],
  },
  {
    id: "sales_team", label: "Employees", icon: Briefcase, roles: ["super_admin", "admin", "employee"],
    children: [
      { id: "sales_team.all", label: "All Team", roles: ["super_admin", "admin"] },
      { id: "sales_team.performance", label: "Performance", roles: ["super_admin", "admin", "employee"] },
    ],
  },
  { id: "my_attendance", label: "My Attendance", icon: CalendarCheck, roles: ["employee"] },
  {
    id: "attendance", label: "Attendance", icon: UserCheck, roles: ["super_admin", "admin"],
    children: [
      { id: "attendance.today", label: "Today's Roster", roles: ["super_admin", "admin"] },
      { id: "attendance.calendar", label: "Monthly Calendar", roles: ["super_admin", "admin"] },
      { id: "attendance.history", label: "History", roles: ["super_admin", "admin"] },
    ],
  },
  {
    id: "billing", label: "Billing & Financials", icon: Wallet, roles: ["super_admin"],
    children: [
      { id: "billing.bookings", label: "Bookings Ledger", roles: ["super_admin"] },
      { id: "billing.income", label: "Income & Invoice", roles: ["super_admin"] },
      { id: "billing.incentives", label: "Incentive Sheets", roles: ["super_admin"] },
      { id: "billing.payroll", label: "Attendance & Payroll", roles: ["super_admin"] },
      { id: "billing.expenses", label: "Expenses", roles: ["super_admin"] },
      { id: "billing.pnl", label: "Profit & Loss", roles: ["super_admin"] },
      { id: "billing.gst", label: "GST", roles: ["super_admin"] },
    ],
  },
  {
    id: "interior", label: "Interior Design", icon: Palette, roles: ["super_admin", "admin"],
    children: [
      { id: "interior.page", label: "Homepage Content", roles: ["super_admin", "admin"] },
      { id: "interior.packages", label: "Packages", roles: ["super_admin", "admin"] },
      { id: "interior.portfolio", label: "Portfolio", roles: ["super_admin", "admin"] },
      { id: "interior.designers", label: "Designers", roles: ["super_admin", "admin"] },
      { id: "interior.leads", label: "Interior Leads", roles: ["super_admin", "admin"] },
    ],
  },
  {
    id: "cms", label: "CMS", icon: FileText, roles: ["super_admin", "admin"],
    children: [
      { id: "cms.homepage", label: "Homepage Sections", roles: ["super_admin", "admin"] },
      { id: "cms.featured", label: "Featured Residences", roles: ["super_admin", "admin"] },
      { id: "cms.team", label: "Team (Meet the Team)", roles: ["super_admin", "admin"] },
      { id: "cms.hero-stats", label: "Hero Stats", roles: ["super_admin", "admin"] },
      { id: "cms.testimonials", label: "Testimonials", roles: ["super_admin", "admin"] },
      { id: "cms.faqs", label: "FAQs", roles: ["super_admin", "admin"] },
      { id: "cms.contact", label: "Contact Info", roles: ["super_admin", "admin"] },
    ],
  },
  { id: "blog", label: "Blog", icon: FileText, roles: ["super_admin", "admin"] },
  { id: "seo", label: "SEO", icon: Search, roles: ["super_admin", "admin"] },
  { id: "media", label: "Media Library", icon: ImageIcon, roles: ["super_admin", "admin"] },
  { id: "analytics", label: "Analytics", icon: BarChart3, roles: ["super_admin", "admin"] },
  { id: "settings", label: "Settings", icon: Settings, roles: ["super_admin"] },
];
